import React, { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, Loader2, MessageSquare, Pencil, Trash2,
  Pin, Lock, Unlock, ArrowRightLeft, Ban, Search, RefreshCw, 
  Ticket, MessageCircle, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const ForumAdmin = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("posts");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [expandedPost, setExpandedPost] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [editingReply, setEditingReply] = useState(null);
  const [editReplyContent, setEditReplyContent] = useState("");
  const [editReplyPostId, setEditReplyPostId] = useState(null);

  const categories = [
    { id: "matchday", name: "Match Day", color: "bg-green-500", icon: Calendar },
    { id: "transfers", name: "Transfers", color: "bg-blue-500", icon: RefreshCw },
    { id: "tickets", name: "Tickets", color: "bg-amber-500", icon: Ticket },
    { id: "general", name: "General", color: "bg-[#7C2C3B]", icon: MessageCircle }
  ];

  useEffect(() => {
    fetchData();
  }, [categoryFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, postsRes, bansRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/forum/stats`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/forum/posts${categoryFilter !== 'all' ? `?category=${categoryFilter}` : ''}`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/forum/bans`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      setStats(statsRes.data);
      // Handle both array and object with posts property
      setPosts(Array.isArray(postsRes.data) ? postsRes.data : (postsRes.data?.posts || []));
      setBannedUsers(Array.isArray(bansRes.data) ? bansRes.data : (bansRes.data?.bans || []));
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || []));
    } catch (error) {
      console.error("Failed to fetch forum data:", error);
      toast.error("Failed to load forum data");
    } finally {
      setLoading(false);
    }
  };

  const handlePinPost = async (postId) => {
    try {
      const res = await axios.post(`${API}/admin/forum/posts/${postId}/pin`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to pin/unpin post");
    }
  };

  const handleLockPost = async (postId) => {
    try {
      const res = await axios.post(`${API}/admin/forum/posts/${postId}/lock`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to lock/unlock post");
    }
  };

  const handleMovePost = async (postId, newCategory) => {
    try {
      const res = await axios.post(`${API}/admin/forum/posts/${postId}/move?category=${newCategory}`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to move post");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm("Are you sure you want to delete this post? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/forum/${postId}`, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Post deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleDeleteReply = async (postId, replyId) => {
    if (!confirm("Delete this reply?")) return;
    try {
      await axios.delete(`${API}/admin/forum/posts/${postId}/replies/${replyId}`, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Reply deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete reply");
    }
  };

  const handleEditReply = async () => {
    if (!editingReply || !editReplyContent.trim()) return;
    try {
      await axios.put(`${API}/admin/forum/posts/${editReplyPostId}/replies/${editingReply}`, 
        { content: editReplyContent },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Reply updated");
      setEditingReply(null);
      setEditReplyContent("");
      setEditReplyPostId(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update reply");
    }
  };

  const handleUpdatePost = async () => {
    if (!editingPost) return;
    try {
      await axios.put(`${API}/admin/forum/posts/${editingPost.id}`, {
        title: editingPost.title,
        content: editingPost.content,
        category: editingPost.category
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Post updated");
      setEditDialogOpen(false);
      setEditingPost(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update post");
    }
  };

  const handleBanUser = async () => {
    if (!selectedUser) return;
    try {
      await axios.post(`${API}/admin/forum/bans/${selectedUser.id}?reason=${encodeURIComponent(banReason || 'Violation of community guidelines')}`, {}, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(`${selectedUser.full_name} has been banned from the forum`);
      setBanDialogOpen(false);
      setSelectedUser(null);
      setBanReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to ban user");
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/forum/bans/${userId}`, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("User unbanned");
      fetchData();
    } catch (error) {
      toast.error("Failed to unban user");
    }
  };

  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    return post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           post.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           post.user_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getCategoryBadge = (catId) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return null;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white ${cat.color}`}>
        <cat.icon className="w-3 h-3" />
        {cat.name}
      </span>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="admin-forum">
      <h1 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
        <MessageSquare className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
        FORUM MANAGEMENT
      </h1>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-[#0F172A]">{stats?.total_posts || 0}</div>
          <div className="text-xs text-slate-500">Total Posts</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-blue-600">{stats?.total_replies || 0}</div>
          <div className="text-xs text-slate-500">Total Replies</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-amber-600">{stats?.pinned_posts || 0}</div>
          <div className="text-xs text-slate-500">Pinned</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-slate-600">{stats?.locked_posts || 0}</div>
          <div className="text-xs text-slate-500">Locked</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-red-600">{stats?.banned_users || 0}</div>
          <div className="text-xs text-slate-500">Banned Users</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-green-600">{stats?.recent_posts_24h || 0}</div>
          <div className="text-xs text-slate-500">Last 24h</div>
        </div>
      </div>

      {/* Category Stats */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {categories.map(cat => (
          <div key={cat.id} className={`rounded-xl p-3 ${cat.color} bg-opacity-10 border border-opacity-20`}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center`}>
                <cat.icon className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-800">{stats?.category_stats?.[cat.id] || 0}</div>
                <div className="text-xs text-slate-500">{cat.name}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="posts" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
            <MessageSquare className="w-4 h-4 mr-2" />Posts ({posts.length})
          </TabsTrigger>
          <TabsTrigger value="bans" className="data-[state=active]:bg-[#7C2C3B] data-[state=active]:text-white">
            <Ban className="w-4 h-4 mr-2" />Banned Users ({bannedUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* Posts Tab */}
        <TabsContent value="posts">
          {/* Search and Filter */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search posts by title, content, or author..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={fetchData} variant="outline" size="icon">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Posts Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Post</TableHead>
                  <TableHead className="w-28">Category</TableHead>
                  <TableHead className="w-32">Author</TableHead>
                  <TableHead className="w-20 text-center">Replies</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-48 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPosts.map(post => (
                  <React.Fragment key={post.id}>
                    <TableRow className={post.is_pinned ? "bg-amber-50" : ""}>
                      <TableCell>
                        <button onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}>
                          {expandedPost === post.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-md">
                          <p className="font-medium text-slate-800 truncate">{post.title}</p>
                          <p className="text-xs text-slate-500 truncate">{post.content?.substring(0, 80)}...</p>
                        </div>
                      </TableCell>
                      <TableCell>{getCategoryBadge(post.category)}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{post.user_name}</p>
                        <p className="text-xs text-slate-400">{new Date(post.created_at).toLocaleDateString()}</p>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-sm font-medium">
                          {post.reply_count || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {post.is_pinned && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">
                              <Pin className="w-3 h-3" />Pinned
                            </span>
                          )}
                          {post.is_locked && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">
                              <Lock className="w-3 h-3" />Locked
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end">
                          <Button 
                            onClick={() => handlePinPost(post.id)} 
                            variant="ghost" 
                            size="icon" 
                            className={`h-8 w-8 ${post.is_pinned ? 'text-amber-600' : ''}`}
                            title={post.is_pinned ? "Unpin" : "Pin"}
                          >
                            <Pin className="w-4 h-4" />
                          </Button>
                          <Button 
                            onClick={() => handleLockPost(post.id)} 
                            variant="ghost" 
                            size="icon"
                            className={`h-8 w-8 ${post.is_locked ? 'text-slate-600' : ''}`}
                            title={post.is_locked ? "Unlock" : "Lock"}
                          >
                            {post.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                          </Button>
                          <Select onValueChange={(val) => handleMovePost(post.id, val)}>
                            <SelectTrigger className="h-8 w-8 p-0 border-none">
                              <ArrowRightLeft className="w-4 h-4 mx-auto" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.filter(c => c.id !== post.category).map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>Move to {cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            onClick={() => { setEditingPost({...post}); setEditDialogOpen(true); }} 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            onClick={() => handleDeletePost(post.id)} 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Expanded Replies */}
                    {expandedPost === post.id && post.replies?.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-slate-50 p-4">
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-slate-600 mb-3">Replies ({post.replies.length})</p>
                            {post.replies.map(reply => (
                              <div key={reply.id} className="flex items-start justify-between bg-white p-3 rounded-lg border border-slate-200">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-sm">{reply.user_name}</span>
                                    <span className="text-xs text-slate-400">{new Date(reply.created_at).toLocaleString()}</span>
                                    {reply.edited_at && <span className="text-xs text-blue-400">(edited)</span>}
                                  </div>
                                  {editingReply === reply.id ? (
                                    <div className="flex gap-2 mt-1">
                                      <Input
                                        value={editReplyContent}
                                        onChange={(e) => setEditReplyContent(e.target.value)}
                                        className="flex-1 text-sm"
                                        data-testid={`edit-reply-input-${reply.id}`}
                                      />
                                      <Button size="sm" onClick={handleEditReply} className="bg-[#7C2C3B] hover:bg-[#63232F] h-8" data-testid={`save-reply-btn-${reply.id}`}>Save</Button>
                                      <Button size="sm" variant="outline" onClick={() => { setEditingReply(null); setEditReplyContent(""); }} className="h-8">Cancel</Button>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-slate-600">{reply.content}</p>
                                  )}
                                </div>
                                <div className="flex gap-1 ml-2">
                                  <Button
                                    onClick={() => { setEditingReply(reply.id); setEditReplyContent(reply.content); setEditReplyPostId(post.id); }}
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                                    data-testid={`edit-reply-${reply.id}`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteReply(post.id, reply.id)}
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                                    data-testid={`delete-reply-${reply.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            {filteredPosts.length === 0 && (
              <div className="text-center py-12 text-slate-500">No posts found</div>
            )}
          </div>
        </TabsContent>

        {/* Banned Users Tab */}
        <TabsContent value="bans">
          <div className="flex justify-end mb-4">
            <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700">
                  <Ban className="w-4 h-4 mr-2" /> BAN USER
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ban User from Forum</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm font-medium">Select User</label>
                    <Select onValueChange={(val) => setSelectedUser(users.find(u => u.id === val))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Choose a user to ban" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => !u.is_admin && !bannedUsers.find(b => b.user_id === u.id)).map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Reason for Ban</label>
                    <Textarea
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value)}
                      placeholder="Violation of community guidelines..."
                      className="mt-1"
                    />
                  </div>
                  <Button onClick={handleBanUser} className="w-full bg-red-600 hover:bg-red-700" disabled={!selectedUser}>
                    <Ban className="w-4 h-4 mr-2" /> CONFIRM BAN
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Banned By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bannedUsers.map(ban => (
                  <TableRow key={ban.id}>
                    <TableCell className="font-medium">{ban.user_name}</TableCell>
                    <TableCell className="text-slate-500">{ban.user_email}</TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">{ban.reason}</span>
                    </TableCell>
                    <TableCell className="text-sm">{ban.banned_by}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(ban.banned_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => handleUnbanUser(ban.user_id)}
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-300 hover:bg-green-50"
                      >
                        <Unlock className="w-3 h-3 mr-1" /> Unban
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {bannedUsers.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Ban className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No banned users</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Post Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          {editingPost && (
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium">Title</label>
                <Input
                  value={editingPost.title}
                  onChange={(e) => setEditingPost({...editingPost, title: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Content</label>
                <Textarea
                  value={editingPost.content}
                  onChange={(e) => setEditingPost({...editingPost, content: e.target.value})}
                  rows={5}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <Select value={editingPost.category} onValueChange={(val) => setEditingPost({...editingPost, category: val})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpdatePost} className="w-full bg-[#7C2C3B] hover:bg-[#63232F]">
                SAVE CHANGES
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForumAdmin;
